import React, { useMemo } from 'react';
import EventFlyerMedia from '../EventFlyerMedia';
import EventAssigneeAvatars from './EventAssigneeAvatars';

const WEEKDAYS_EN = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const formatTime = (timeString) => {
  if (!timeString) return '';
  return String(timeString).slice(0, 5);
};

const resolveDateParts = (dateString) => {
  if (!dateString) return null;
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return null;
  const now = new Date();
  const yearLabel = parsed.getFullYear() !== now.getFullYear() ? String(parsed.getFullYear()) : '';
  const monthLabel = String(parsed.getMonth() + 1);
  const weekdayLabel = WEEKDAYS_EN[parsed.getDay()] || '';
  const weekdayClass = parsed.getDay() === 0 ? 'event-card-date-meta--sun' : parsed.getDay() === 6 ? 'event-card-date-meta--sat' : 'event-card-date-meta--week';
  return {
    day: String(parsed.getDate()),
    yearLabel,
    monthLabel,
    dayLabel: String(parsed.getDate()),
    weekdayLabel,
    weekdayClass,
  };
};

const splitTitleByDelimiter = (text, limit) => {
  const value = (text || '').trim();
  if (!value) return { line1: '', line2: '' };
  if (value.length <= limit) return { line1: value, line2: '' };

  const splitAfterTokens = [',', '，', '、', '・', '/', '／', '|', '｜', '-', '–', '—', ':', '：', ';', '；'];
  const splitBeforeTokens = ['~', '～', '〜', '@', '＠'];
  const spaceTokens = [' ', '　'];

  let best = null;
  const consider = (index) => {
    if (index <= 0 || index >= value.length) return;
    const score = Math.abs(index - limit);
    if (!best || score < best.score || (score === best.score && index <= limit)) {
      best = { index, score };
    }
  };

  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (splitAfterTokens.includes(ch)) consider(i + 1);
    if (splitBeforeTokens.includes(ch)) consider(i);
    if (spaceTokens.includes(ch)) consider(i);
  }

  const index = best ? best.index : Math.min(limit, value.length - 1);
  const line1 = value.slice(0, index).trimEnd();
  const line2 = value.slice(index).trimStart();
  if (!line2) return { line1: value, line2: '' };
  return { line1, line2 };
};

const buildTitleLines = (name, performance, isCompact) => {
  const title = (name || '').trim();
  const perf = (performance || '').trim();
  if (!title && !perf) return { line1: '', line2: '', line2Type: 'none' };
  if (!title) return { line1: perf, line2: '', line2Type: 'none' };
  if (!perf) {
    const limit = isCompact ? 14 : 18;
    const split = splitTitleByDelimiter(title, limit);
    return { ...split, line2Type: split.line2 ? 'title' : 'none' };
  }
  const combined = `${title} ${perf}`;
  const combinedLimit = isCompact ? 16 : 20;
  if (combined.length > combinedLimit) {
    return { line1: title, line2: perf, line2Type: 'performance' };
  }
  return { line1: combined, line2: '', line2Type: 'none' };
};

const clampPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, Math.round(num)));
};

const isPastEvent = (event) => {
  if (!event) return false;
  const now = new Date();
  const toDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      date.setHours(23, 59, 59, 999);
    }
    return date;
  };
  const endCandidate =
    event.event_end_at || event.event_end_date || event.event_end || event.end_at;
  if (endCandidate) {
    const endDate = toDate(endCandidate);
    if (endDate) return endDate.getTime() < now.getTime();
  }
  if (!event.event_date) return false;
  const date = toDate(event.event_date);
  if (!date) return false;
  return date.getTime() < now.getTime();
};

const EventListCard = ({
  event,
  imgSrc,
  imgAlt,
  isLandscape,
  onFlyerLoad,
  onFlyerError,
  onFlyerDisplayTimeout,
  flyerReveal,
  flyerDelayMs = 0,
  flyerDisplayTimeoutMs = 0,
  flyerLoading = 'lazy',
  flyerFetchPriority,
  onClick,
  isToday,
  todayLabel,
  isCancelled,
  venueDisplay,
  badges,
  infoIcons,
  progressItems = [],
  isCompact,
  placeholderDesignKey = '',
  placeholderColorKey = '',
  noticeLabel = '',
  navigationMenu = /** @type {import('react').ReactNode} */ (null),
}) => {
  const dateParts = useMemo(() => resolveDateParts(event?.event_date), [event?.event_date]);
  const titleLines = useMemo(
    () => buildTitleLines(event?.name, event?.performance_type, isCompact),
    [event?.name, event?.performance_type, isCompact]
  );
  const openTime = formatTime(event?.open_time);
  const startTime = formatTime(event?.start_time);
  const showProgress = Array.isArray(progressItems) && progressItems.length > 0;
  const isPast = useMemo(() => isPastEvent(event), [event]);
  const shouldFadeFlyer = typeof flyerReveal === 'boolean';
  const flyerMediaClassName = shouldFadeFlyer
    ? `event-flyer-media--fade${flyerReveal ? ' is-visible' : ''}`
    : '';
  const flyerMediaStyle = shouldFadeFlyer && flyerDelayMs
    ? { transitionDelay: `${flyerDelayMs}ms` }
    : undefined;
  const flyerClassName = `event-image${isLandscape ? ' is-landscape' : ''}`;

  return (
    <div
      className={`event-card${isToday ? ' is-today' : ''}${isCancelled ? ' is-cancelled' : ''}${
        isCompact ? ' is-compact' : ''
      }`}
      onClick={onClick}
    >
      {isToday && todayLabel && <span className="today-badge" aria-label={todayLabel}>{todayLabel}</span>}
      {navigationMenu}

      <div
        className={`event-card-media${flyerMediaClassName ? ` ${flyerMediaClassName}` : ''}`}
        style={flyerMediaStyle}
      >
        <EventFlyerMedia
          event={event}
          imageUrl={imgSrc}
          imageAlt={imgAlt || 'Flyer'}
          sizeVariant="card"
          borderRadius={0}
          placeholderClassName="event-image-placeholder"
          imageClassName={flyerClassName}
          hasImage={Boolean(imgSrc)}
          visualMode={imgSrc && isLandscape ? 'solid' : 'decorated'}
          placeholderDesignKey={placeholderDesignKey}
          placeholderColorKey={placeholderColorKey}
          onImageLoad={(target) => onFlyerLoad && onFlyerLoad(target)}
          onDisplayTimeout={() => onFlyerDisplayTimeout && onFlyerDisplayTimeout()}
          displayTimeoutMs={flyerDisplayTimeoutMs}
          loading={flyerLoading}
          fetchPriority={flyerFetchPriority}
          onImageError={(target) => {
            if (onFlyerError) onFlyerError(target);
            target.onerror = null;
            target.style.display = 'none';
          }}
        />
        {(badges || infoIcons) && (
          <div className="event-card-genre-stack">
            {badges}
            {infoIcons}
          </div>
        )}
      </div>

      <div className="event-card-body">
        <div className="event-card-header">
          <div className="event-card-title-block">
            <div className={`event-card-title${isCancelled ? ' is-cancelled' : ''}`}>
              {titleLines.line1 && (
                <span className={`event-card-title-line${noticeLabel ? ' has-notice' : ''}`}>
                  {noticeLabel ? (
                    <span className="event-card-notice-mark" aria-label={noticeLabel} title={noticeLabel}>
                      <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    </span>
                  ) : null}
                  {event?.partner_shared && <span>パートナー共有　</span>}{titleLines.line1}
                </span>
              )}
              {titleLines.line2 && (
                <span
                  className={`event-card-title-line${titleLines.line2Type === 'performance' ? ' is-performance' : ''}`}
                >
                  {titleLines.line2}
                </span>
              )}
            </div>
          </div>

          {dateParts && (
            <div
              className="event-card-date"
              aria-label={`${dateParts.yearLabel ? `${dateParts.yearLabel}/` : ''}${dateParts.monthLabel}/${dateParts.dayLabel} ${dateParts.weekdayLabel}`.trim()}
            >
              {dateParts.yearLabel && (
                <span className="event-card-date-year">{dateParts.yearLabel}</span>
              )}
              <div className="event-card-date-row">
                <span className="event-card-date-month">{dateParts.monthLabel}</span>
                <span className="event-card-date-slash">/</span>
                <span className="event-card-date-day">{dateParts.dayLabel}</span>
              </div>
              <span className={`event-card-date-meta ${dateParts.weekdayClass}`}>
                {dateParts.weekdayLabel}
              </span>
            </div>
          )}
          {(venueDisplay?.name || venueDisplay?.link) && (
            <div className="event-card-venue-line">
              <i className="fa-solid fa-location-dot event-card-venue-icon" aria-hidden="true"></i>
              {venueDisplay?.link ? (
                <a
                  className="event-card-venue"
                  href={venueDisplay.link}
                  onClick={(e) => e.stopPropagation()}
                  title={venueDisplay?.name || undefined}
                >
                  {venueDisplay?.name}
                </a>
              ) : (
                <span className="event-card-venue" title={venueDisplay?.name || undefined}>
                  {venueDisplay?.name}
                </span>
              )}
            </div>
          )}
          <EventAssigneeAvatars
            assignees={event?.assigned_admins || event?.assignedAdmins}
            className="event-card-assignees"
          />
          {(openTime || startTime) && (
            <div className="event-card-time">
              {openTime && (
                <span className="event-card-time-item">
                  <span className="event-card-time-label">OPEN</span>
                  <span className="event-card-time-value">{openTime}</span>
                </span>
              )}
              {openTime && startTime && <span className="event-card-time-sep">/</span>}
              {startTime && (
                <span className="event-card-time-item">
                  <span className="event-card-time-label">START</span>
                  <span className="event-card-time-value">{startTime}</span>
                </span>
              )}
            </div>
          )}
        </div>

        {showProgress && (
          <div className="event-card-progress" aria-label="Progress">
            {progressItems.map((item) => {
              const percent = clampPercent(item?.percent);
              return (
                <div key={item?.key || item?.label} className="event-card-progress-item">
                  <div className="event-card-progress-labels">
                    <span className="event-card-progress-label">
                      <i
                        className={`fa-solid ${
                          item?.tone === 'ticket'
                            ? 'fa-ticket'
                            : item?.tone === 'task'
                            ? 'fa-list-check'
                            : 'fa-user'
                        } event-card-progress-icon event-card-progress-icon--${item?.tone || 'default'}`}
                        aria-hidden="true"
                      ></i>
                      <span className="event-card-progress-text">{item?.label}</span>
                      {item?.note && <span className="event-card-progress-note">({item.note})</span>}
                    </span>
                    <span className={`event-card-progress-value event-card-progress-value--${item?.tone || 'default'}`}>
                      {item?.valueLabel || `${percent}%`}
                    </span>
                  </div>
                  <div className="event-card-progress-track" aria-label={`${item?.label || 'Progress'} ${percent}%`}>
                    <div
                      className={`event-card-progress-fill event-card-progress-fill--${item?.tone || 'default'}${isPast ? ' is-static' : ''}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export { formatTime, resolveDateParts, buildTitleLines, clampPercent, isPastEvent };
export default EventListCard;
